// src/api/games.js
import { parseNaturalLanguageQuery } from '../utils/naturalLanguageSearch';

// Get the API URL from environment or use relative URL for production
const API_URL = process.env.NODE_ENV === 'production'
  ? '' // Empty string means use relative URLs
  : 'http://localhost:3001';


/**
 * Search for games using either structured filters or natural language
 * @param {Object} params - Search parameters
 * @returns {Promise<Array>} - Array of games matching criteria
 */
export async function searchGames(params, page = 1) {
  try {
    console.log('SearchGames called with params:', params, 'page:', page);
    const searchParams = new URLSearchParams();

    // Add page parameter
    searchParams.append('page', page);
    
    // Check if this is a natural language query
    if (params.naturalQuery) {
      console.log('Natural language query detected:', params.naturalQuery);
      
      // Instead of sending the raw NL query to the server,
      // parse it client-side and send structured parameters
      const parsedQuery = parseNaturalLanguageQuery(params.naturalQuery);
      console.log('Parsed natural query into structured params:', parsedQuery);
      
      // Merge all structured filters with natural language parsed filters
      if (params.players !== undefined && params.players !== '' && !parsedQuery.players) {
        parsedQuery.players = params.players;
      }
      if (params.maxTime && !parsedQuery.maxTime) {
        parsedQuery.maxTime = params.maxTime;
      }
      if (params.minAge && !parsedQuery.minAge) {
        parsedQuery.minAge = params.minAge;
      }
      if (params.weight && !parsedQuery.weight) {
        parsedQuery.weight = params.weight;
      }
      if (params.themes?.length) {
        if (!parsedQuery.themes) {
          parsedQuery.themes = [...params.themes];
        } else {
          // Create combined set of unique themes
          const allThemes = new Set([...parsedQuery.themes, ...params.themes]);
          parsedQuery.themes = Array.from(allThemes);
        }
        console.log('Combined themes:', parsedQuery.themes);
      }
      if (params.mechanics?.length && (!parsedQuery.mechanics || !parsedQuery.mechanics.length)) {
        parsedQuery.mechanics = params.mechanics;
      }

      console.log('Merged query params:', parsedQuery);

      // Merge structured filters with natural language parsed filters
      if (params.players !== undefined && !parsedQuery.players) {
        parsedQuery.players = params.players;
        console.log('Adding players from structured filters:', params.players);
      }

      // Add all parsed parameters to the search params
      if (parsedQuery.searchTerm) {
        searchParams.append('search', parsedQuery.searchTerm);
        console.log('Setting search term from NL:', parsedQuery.searchTerm);
      }
      
      // Handle player counts
      if (parsedQuery.players) {
        searchParams.append('players', parsedQuery.players);
        console.log('Setting players from NL:', parsedQuery.players);
      }
      
      
      // Add other parameters
      if (parsedQuery.maxTime) {
        searchParams.append('maxTime', parsedQuery.maxTime);
        console.log('Setting maxTime from NL:', parsedQuery.maxTime);
      }
      if (parsedQuery.minAge) {
        searchParams.append('minAge', parsedQuery.minAge);
        console.log('Setting minAge from NL:', parsedQuery.minAge);
      }
      
      if (parsedQuery.weight) {
        searchParams.append('weight', parsedQuery.weight);
        console.log('Setting weight from NL:', parsedQuery.weight);
      }
      
      if (parsedQuery.themes?.length) {
        const themesJson = JSON.stringify(parsedQuery.themes);
        searchParams.append('themes', themesJson);
        console.log('Setting themes param:', themesJson);
      }
            
      if (parsedQuery.mechanics?.length) {
        searchParams.append('mechanics', JSON.stringify(parsedQuery.mechanics));
        console.log('Setting mechanics from NL:', parsedQuery.mechanics);
      }
    } else {
      // Use traditional structured filters
      console.log('Using traditional structured filters');
      
      if (params.search) {
        searchParams.append('search', params.search);
        console.log('Setting search term:', params.search);
      }

      // Handle players
      if (params.players) {
        searchParams.append('players', params.players);
        console.log('Setting players:', params.players);
      }
      
      if (params.maxTime) {
        searchParams.append('maxTime', params.maxTime);
        console.log('Setting maxTime:', params.maxTime);
      }
      if (params.minAge) {
        searchParams.append('minAge', params.minAge);
        console.log('Setting minAge:', params.minAge);
      }
      
      // Handle multiple weight values for a difficulty level
      if (params.weight) {
        if (Array.isArray(params.weight)) {
          searchParams.append('weight', JSON.stringify(params.weight));
          console.log('Setting weight (array):', params.weight);
        } else {
          searchParams.append('weight', params.weight);
          console.log('Setting weight:', params.weight);
        }
      }
      
      if (params.themes?.length) {
        searchParams.append('themes', JSON.stringify(params.themes));
        console.log('Setting themes:', params.themes);
      }
      if (params.mechanics?.length) {
        searchParams.append('mechanics', JSON.stringify(params.mechanics));
        console.log('Setting mechanics:', params.mechanics);
      }
    }
 
    // Log the final URLSearchParams object before creating the URL
    console.log('Final searchParams entries:');
    for (const [key, value] of searchParams.entries()) {
      console.log(`  ${key}: ${value}`);
    }

    const url = `${API_URL}/api/games?${searchParams}`;
    console.log('API request URL:', url);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const results = await response.json();
    console.log(`Received ${results.length} games from API`);
    return results;
  } catch (error) {
    console.error('Error fetching games:', error);
    throw error;
  }
}